import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { OVERALL_STATUS_LABELS, PROGRAM_STATUS_LABELS, statusColor } from '../qualificationApi';
import ScenarioActions from './ScenarioActions';

const fmt$ = (n) => (n || n === 0) ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '—';
const pct = (n) => (n || n === 0) ? `${Number(n).toFixed(1)}%` : '—';

const SEVERITY_LABELS = { hard_stop: 'Hard Stop', soft_stop: 'Soft Stop', warning: 'Warning' };
const SEVERITY_COLOR = {
  hard_stop: 'text-red-700 bg-red-50 border-red-200',
  soft_stop: 'text-amber-700 bg-amber-50 border-amber-200',
  warning: 'text-slate-600 bg-slate-100 border-slate-200',
};

function StatusChip({ status, labels, testid }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${statusColor(status)}`}
      data-testid={testid}
    >
      {labels[status] || status}
    </span>
  );
}

// Result of a /calculate (or saved scenario.result) call. `compact` trims the
// per-program detail for use inside the live preview panel.
export default function QualificationResults({ result, scenario, compact = false, loanId, contactId }) {
  if (!result) return null;
  return (
    <div className="space-y-4" data-testid="qualification-results">
      <div className="flex flex-wrap items-center gap-2">
        <StatusChip status={result.overall_status} labels={OVERALL_STATUS_LABELS} testid="results-status" />
        {result.limits_unverified && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700">
            <AlertTriangle size={11} /> Limits unverified
          </span>
        )}
        {result.credit_estimated && (
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">
            Credit estimated
          </span>
        )}
      </div>

      <div className={`grid gap-3 ${compact ? '' : 'md:grid-cols-2'}`}>
        {(result.programs || []).map((p) => (
          <div key={p.code} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`program-card-${p.code}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold" style={{ color: '#070B5F' }}>{p.name}</div>
              <StatusChip status={p.status} labels={PROGRAM_STATUS_LABELS} testid={`program-status-${p.code}`} />
            </div>
            <div className="text-xs text-slate-500 grid grid-cols-2 gap-y-1">
              <div>Rate</div><div className="text-right font-medium text-slate-700">{pct(p.rate_used)}</div>
              {p.financed_fee > 0 ? (
                <>
                  <div>Base loan</div><div className="text-right font-medium text-slate-700">{fmt$(p.loan_amount)}</div>
                  <div>Upfront fee financed</div><div className="text-right font-medium text-slate-700">{fmt$(p.financed_fee)}</div>
                  <div>Total loan</div><div className="text-right font-medium text-slate-700">{fmt$(p.total_loan_amount)}</div>
                </>
              ) : (
                <><div>Loan amount</div><div className="text-right font-medium text-slate-700">{fmt$(p.loan_amount)}</div></>
              )}
              <div>LTV</div><div className="text-right font-medium text-slate-700">{pct(p.ltv)}</div>
              {!compact && (
                <>
                  <div>P&amp;I</div><div className="text-right font-medium text-slate-700">{fmt$(p.pi)}</div>
                  <div>Tax</div><div className="text-right font-medium text-slate-700">{fmt$(p.tax)}</div>
                  <div>Insurance</div><div className="text-right font-medium text-slate-700">{fmt$(p.insurance)}</div>
                  <div>MI</div><div className="text-right font-medium text-slate-700">{fmt$(p.mi)}</div>
                  <div>HOA</div><div className="text-right font-medium text-slate-700">{fmt$(p.hoa)}</div>
                </>
              )}
              <div className="font-semibold text-slate-700">Total payment</div>
              <div className="text-right font-semibold" style={{ color: '#6BA82E' }}>{fmt$(p.total_payment)}</div>
              <div>DTI</div><div className="text-right font-medium text-slate-700">{pct(p.dti)}</div>
              <div>Max purchase price</div><div className="text-right font-medium text-slate-700">{fmt$(p.max_purchase_price)}</div>
            </div>
            {!compact && p.reasons?.length > 0 && (
              <div className="mt-3 pt-2 border-t border-slate-100">
                <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">Reasons</div>
                <ul className="text-xs text-slate-600 list-disc pl-4 space-y-0.5">
                  {p.reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
            {!compact && p.conditions?.length > 0 && (
              <div className="mt-2">
                <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">Conditions</div>
                <ul className="text-xs text-slate-600 list-disc pl-4 space-y-0.5">
                  {p.conditions.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}
            {!compact && scenario && (
              <ScenarioActions scenario={scenario} program={p} loanId={loanId} contactId={contactId} />
            )}
          </div>
        ))}
      </div>

      {!compact && result.disqualifying_factors?.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="disqualifying-factors">
          <div className="text-sm font-semibold mb-2" style={{ color: '#070B5F' }}>Disqualifying factors</div>
          <div className="space-y-2">
            {result.disqualifying_factors.map((f, i) => (
              <div key={i} className="text-xs border border-slate-100 rounded-lg p-2">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-slate-700">{f.factor}</span>
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${SEVERITY_COLOR[f.severity]}`}>
                    {SEVERITY_LABELS[f.severity] || f.severity}
                  </span>
                  {f.program_code && <span className="text-slate-400">{f.program_code}</span>}
                </div>
                <div className="text-slate-600">{f.explanation}</div>
                {f.remediation && <div className="text-slate-400 mt-0.5">Remediation: {f.remediation}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {!compact && result.disclaimer && (
        <p className="text-[11px] text-slate-400" data-testid="results-disclaimer">{result.disclaimer}</p>
      )}
    </div>
  );
}
